import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet, apiPost, encodePost, type PostValue } from './client'
import { saveBytes } from './inventoree'

/**
 * Settings -> Reports -> BI Reports (ITIL), calea noua.
 *
 * Perechile vechi, toate din `settings/Company_reports`:
 *
 *   bi_report / bi_settings_report -> GET  /settings/company-reports/bi
 *   getBiReportData                -> POST .../bi/run
 *   previewBiReport                -> POST .../bi/preview
 *   biSuggest                      -> POST .../bi/suggest
 *   askBiReport                    -> POST .../bi/ask
 *   saveBiCustomReport             -> POST .../bi/custom
 *   deleteBiCustomReport           -> POST .../bi/custom/{id}/remove
 *   generateBiReportExcel          -> GET  .../bi/export.xlsx
 *   generateBiReportPdf            -> GET  .../bi/export.pdf
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * Exporturile nu mai trec prin `download_script_file`: fisierul vine direct
 * in raspuns si se salveaza din pagina.
 */

export type BiOption = { value: string; label: string }

/** Un control de filtru al unui raport din registru (`filters` din catalog). */
export type BiFilter = {
  name: string
  type: 'date' | 'period' | 'number' | 'select' | 'multiselect' | 'text'
  label: string
  required?: boolean
  options?: BiOption[]
  operators?: BiOption[]
  suggest?: string
}

export type BiColumnSpec = { field: string; agg: string; bucket: string }

/** Definitia normalizata a unui raport din constructor, cum o tine serverul. */
export type BiDefinition = {
  columns: BiColumnSpec[]
  filters: string[]
  sort: { by: string; dir: 'asc' | 'desc' }
  group: number
}

/** O intrare de registru: predefinita, sau salvata din constructor (`custom`). */
export type BiCatalogEntry = {
  label: string
  group: string
  note: string
  filters: BiFilter[]
  /** camp -> eticheta, in ordinea coloanelor. */
  columns: Record<string, string>
  itil?: string[]
  has_data: boolean
  custom?: {
    id: number
    name: string
    dataset: string
    definition: BiDefinition
    roles: number[]
  }
}

export type BiAggregate = { value: string; label: string; template: string }

/** Un camp al unei surse de date, asa cum il vede constructorul. */
export type BiField = {
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'select'
  filterable: boolean
  required: boolean
  options: BiOption[]
  suggest: string
  aggregates: BiAggregate[]
  buckets: string[]
}

export type BiDataset = {
  label: string
  note: string
  has_data: boolean
  fields: BiField[]
}

export type BiPage = {
  pageTitle: string
  pageDescription: string
  moduleUrl: string
  limit: number
  catalog: Record<string, BiCatalogEntry>
  coverage: Record<string, number>
  builder: Record<string, BiDataset>
  periods: BiOption[]
  operators: BiOption[]
  roles: { id: number; name: string }[]
}

/** Valorile filtrelor: un sir, sau o lista pentru selectiile multiple. */
export type BiFilterValues = Record<string, string | string[]>

export type BiRow = Record<string, string | number | null>

export type BiResult = {
  label?: string
  columns: Record<string, string>
  rows: BiRow[]
  limit: number
  /** De ce e gol - cand motivul e sursa, nu filtrele. */
  empty_note: string
}

export type BiAnswer = BiResult & {
  dataset: string
  definition: BiDefinition
  values: BiFilterValues
  title: string
  note: string
}

export type BiSaved = { key: string; entry: BiCatalogEntry }

export const biReportsPageQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'bi'],
  queryFn: () => apiGet<BiPage>('/settings/company-reports/bi'),
})

/** `filters[nume]=v` si `filters[nume][]=v`, cum le citea si portalul. */
function filterFields(filters: BiFilterValues): Record<string, PostValue> {
  const out: Record<string, PostValue> = {}
  for (const [name, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      out[`filters[${name}]`] = value
    } else {
      out[`filters[${name}]`] = value
    }
  }
  return out
}

export function runBiReport(report: string, filters: BiFilterValues): Promise<BiResult> {
  return apiPost<BiResult>('/settings/company-reports/bi/run', { report, ...filterFields(filters) })
}

/** Definitia pleaca ca JSON intr-un camp, cum o trimitea `JSON.stringify(builderDefinition())`. */
export function previewBiReport(dataset: string, definition: unknown, filters: BiFilterValues): Promise<BiResult> {
  return apiPost<BiResult>('/settings/company-reports/bi/preview', {
    dataset,
    definition: JSON.stringify(definition),
    ...filterFields(filters),
  })
}

export function suggestBi(source: string, q: string): Promise<{ id: string; text: string }[]> {
  return apiPost<{ id: string; text: string }[]>('/settings/company-reports/bi/suggest', { source, q })
}

export function askBiReport(question: string): Promise<BiAnswer> {
  return apiPost<BiAnswer>('/settings/company-reports/bi/ask', { question })
}

export function saveBiCustomReport(id: number, name: string, dataset: string, definition: unknown, roles: string[]): Promise<BiSaved> {
  return apiPost<BiSaved>('/settings/company-reports/bi/custom', {
    id: String(id),
    name,
    dataset,
    definition: JSON.stringify(definition),
    roles,
  })
}

export function deleteBiCustomReport(id: number): Promise<{ ok: number }> {
  return apiPost<{ ok: number }>(`/settings/company-reports/bi/custom/${id}/remove`, {})
}

/** Foaia de calcul, salvata pe disc; o cerere gresita (perioada lipsa) vine ca ApiError cu propozitia ei. */
export async function downloadBiXlsx(report: string, filters: BiFilterValues): Promise<void> {
  const query = encodePost({ report, ...filterFields(filters) }).toString()
  const out = await apiExchange(`/settings/company-reports/bi/export.xlsx?${query}`, { method: 'GET' })
  saveBytes(out.bytes, out.type, out.filename, 'bi_report.xlsx')
}

export async function downloadBiPdf(report: string, filters: BiFilterValues, summary: string): Promise<void> {
  const query = encodePost({ report, summary, ...filterFields(filters) }).toString()
  const out = await apiExchange(`/settings/company-reports/bi/export.pdf?${query}`, { method: 'GET' })
  if (!out.type.includes('pdf')) {
    let message = 'The report PDF could not be produced.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 501, message)
  }
  saveBytes(out.bytes, out.type, out.filename, 'bi_report.pdf')
}
